import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { toast } from 'sonner';
import { useSystemOptions } from '@/contexts/SystemOptionsContext';
import {
  ArrowLeft,
  AlertCircle,
  Copy,
  MoreVertical,
  Trash2,
  TrendingDown,
  Printer,
} from 'lucide-react';
import { AssetDetailHeader } from '@/components/assets/detail/AssetDetailHeader';
import { AssetDetailOverview } from '@/components/assets/detail/AssetDetailOverview';
import { AssetDetailValues } from '@/components/assets/detail/AssetDetailValues';
import { AssetDetailSettings } from '@/components/assets/detail/AssetDetailSettings';
import { AssetDetailSupplier } from '@/components/assets/detail/AssetDetailSupplier';
import { AssetDetailEquipment } from '@/components/assets/detail/AssetDetailEquipment';
import { AssetDetailCustomFields } from '@/components/assets/detail/AssetDetailCustomFields';
import { AssetDetailNotes } from '@/components/assets/detail/AssetDetailNotes';
import { AssetDetailCompliance } from '@/components/assets/detail/AssetDetailCompliance';
import { AssetDetailHistory } from '@/components/assets/detail/AssetDetailHistory';
import { DocumentsTab } from '@/components/documents/documents-tab';
import { AssetAlertsTab } from '@/components/assets/asset-alerts-tab';
import { EvoFeatureFeedbackCollector } from '@/components/FeedbackCollector';
import { dataService } from '@/services/data-service';
import type { Asset } from '@/types/asset';
import { generateAlertsForAsset, calculateAlertSummary } from '@/services/alerts-service';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DisposalWizard } from '@/components/assets/disposal-wizard';
import { LabelPrintingModal } from '@/components/assets/label-printing-modal';

export default function AssetDetail() {
  const { assetId } = useParams<{ assetId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast: legacyToast } = useToast();
  const { options: systemOptions } = useSystemOptions();
  
  const [asset, setAsset] = useState<Asset | null>(null);
  const [editedAsset, setEditedAsset] = useState<Asset | null>(null);
  const [originalPhotos, setOriginalPhotos] = useState<Asset['photos']>(undefined);
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showDisposalWizard, setShowDisposalWizard] = useState(false);
  const [showLabelPrintingModal, setShowLabelPrintingModal] = useState(false);

  // Check if we're in create mode
  const isCreateMode = searchParams.get('mode') === 'create';

  useEffect(() => {
    if (isCreateMode) {
      // Create a new empty asset
      const newAsset: Asset = {
        id: `new-${Date.now()}`,
        code: '',
        name: '',
        description: '',
        category: 'Furniture & Fixtures',
        status: 'inService',
        location: '',
        acquisitionCost: 0,
        netBookValue: 0,
        accumulatedDepreciation: 0,
        currency: 'GBP',
        acquisitionDate: new Date().toISOString().split('T')[0],
        serialNumber: '',
        model: '',
        manufacturer: '',
        createdBy: '',
        createdDate: new Date().toISOString(),
      };
      setAsset(newAsset);
      setEditedAsset(newAsset);
      setIsEditMode(true);
      setLoading(false);
    } else {
      loadAssetDetail();
    }
  }, [assetId, isCreateMode]);

  const loadAssetDetail = async () => {
    if (!assetId) return;
    
    setLoading(true);
    try {
      const data = await dataService.getAsset(assetId);
      setAsset(data);
    } catch (error) {
      console.error('Failed to load asset:', error);
      legacyToast({
        title: 'Error',
        description: 'Failed to load asset details',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    setEditedAsset(asset);
    // Store original photos for incremental image updates
    setOriginalPhotos(asset?.photos ? [...asset.photos] : undefined);
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    if (isCreateMode) {
      navigate(-1);
    } else {
      setEditedAsset(null);
      setOriginalPhotos(undefined);
      setIsEditMode(false);
    }
  };

  const handleSave = async () => {
    if (!editedAsset) return;
    
    // Validate required fields before saving
    let hasErrors = false;
    
    // Code is required (unless auto-numbering is enabled in create mode)
    if (!editedAsset.code && !(isCreateMode && systemOptions?.autoNumbering.enabled)) {
      toast.error('Asset code is required');
      hasErrors = true;
    }
    
    // Name (Description) is required
    if (!editedAsset.name || editedAsset.name.trim() === '') {
      toast.error('Description is required');
      hasErrors = true;
    }
    
    // Category is required
    if (!editedAsset.category || editedAsset.category.trim() === '') {
      toast.error('Category is required');
      hasErrors = true;
    }
    
    // Location is required
    if (!editedAsset.location || editedAsset.location.trim() === '') {
      toast.error('Location is required');
      hasErrors = true;
    }
    
    // Acquisition Cost is required
    if (editedAsset.acquisitionCost === undefined || editedAsset.acquisitionCost === null) {
      toast.error('Acquisition cost is required');
      hasErrors = true;
    } else if (editedAsset.acquisitionCost < 0) {
      toast.error('Acquisition cost cannot be negative');
      hasErrors = true;
    }
    
    // Acquisition Date is required
    if (!editedAsset.acquisitionDate || editedAsset.acquisitionDate.trim() === '') {
      toast.error('Acquisition date is required');
      hasErrors = true;
    }
    
    // If there are validation errors, stop here
    if (hasErrors) {
      return;
    }
    
    setIsSaving(true);
    try {
      if (isCreateMode) {
        // Create new asset
        const createdAsset = await dataService.createAsset(editedAsset);
        setAsset(createdAsset);
        legacyToast({
          title: 'Success',
          description: 'Asset created successfully',
        });
        // Navigate to the new asset's detail page
        navigate(`/assets/${createdAsset.id}`, { replace: true });
      } else {
        // Update existing asset with incremental image updates
        // Pass originalPhotos to enable add/remove individual images without affecting others
        await dataService.updateAsset(editedAsset.id, editedAsset, originalPhotos);
        legacyToast({
          title: 'Success',
          description: 'Asset saved successfully',
        });
        // Navigate back to assets list after saving
        navigate(-1);
        return;
      }
      setIsEditMode(false);
      setEditedAsset(null);
      setOriginalPhotos(undefined);
    } catch (error) {
      console.error('Failed to save asset:', error);
      legacyToast({
        title: 'Error',
        description: 'Failed to save asset. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDuplicate = () => {
    if (!asset) return;
    const duplicatedAsset = {
      ...asset,
      id: `new-${Date.now()}`,
      code: `${asset.code}-COPY`,
    };
    navigate('/assets/new?mode=create', { state: { asset: duplicatedAsset } });
  };

  const handleDelete = async () => {
    if (!asset) return;
    
    try {
      await dataService.deleteAsset(asset.id);
      legacyToast({
        title: 'Success',
        description: 'Asset deleted successfully',
      });
      navigate(-1);
    } catch (error) {
      console.error('Failed to delete asset:', error);
      legacyToast({
        title: 'Error',
        description: 'Failed to delete asset. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDisposal = () => {
    setShowDisposalWizard(true);
  };

  const handlePrintLabel = () => {
    setShowLabelPrintingModal(true);
  };

  const handleAssetUpdate = (updatedAsset: Asset) => {
    if (isEditMode) {
      setEditedAsset(updatedAsset);
    } else {
      setAsset(updatedAsset);
    }
  };

  // Calculate current asset and alerts count BEFORE any conditional returns
  const currentAsset = isEditMode ? editedAsset : asset;
  
  // Calculate alerts count from asset data
  const alertsCount = useMemo(() => {
    if (!currentAsset) return 0;
    const alerts = generateAlertsForAsset(currentAsset);
    const summary = calculateAlertSummary(alerts);
    return summary.totalCount;
  }, [currentAsset]);

  if (loading) {
    return (
      <div className="flex h-full flex-col overflow-hidden">
        <div className="border-b bg-card p-6">
          <Skeleton className="h-8 w-64 mb-4" />
          <Skeleton className="h-6 w-96" />
        </div>
        <div className="flex-1 flex">
          <div className="flex-1 p-6">
            <Skeleton className="h-64" />
          </div>
        </div>
      </div>
    );
  }

  if (!asset && !isCreateMode) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">Asset Not Found</h2>
          <p className="text-muted-foreground mb-4">
            The asset you're looking for doesn't exist or has been deleted.
          </p>
          <Button onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Assets
          </Button>
        </div>
      </div>
    );
  }

  if (!currentAsset) return null;

  const documentsCount = 0; // TODO: Get from asset data
  const photosCount = 0; // TODO: Get from asset data

  return (
    <>
      <div className="flex h-full flex-col">
        {/* Header */}
        <div className="border-b bg-card">
          <div className="px-6 py-4">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(-1)}
                className="h-8 w-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex-1">
                <AssetDetailHeader 
                  asset={currentAsset} 
                  onAssetUpdate={handleAssetUpdate}
                  isEditMode={isEditMode}
                  isCreateMode={isCreateMode}
                />
              </div>
              <div className="flex items-center gap-2">
                {!isCreateMode && (
                  <EvoFeatureFeedbackCollector feature="Asset Detail View" />
                )}
                {!isEditMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={handleEdit}>
                      Edit
                    </Button>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="sm">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={handleDuplicate}>
                          <Copy className="h-4 w-4 mr-2" />
                          Duplicate Asset
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={handlePrintLabel}>
                          <Printer className="h-4 w-4 mr-2" />
                          Print Label
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleDisposal}>
                          <TrendingDown className="h-4 w-4 mr-2" />
                          Dispose Asset
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-destructive"
                          onClick={() => setShowDeleteDialog(true)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete Asset
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSave} disabled={isSaving}>
                      {isSaving ? 'Saving...' : isCreateMode ? 'Create' : 'Save'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 flex overflow-hidden">
          {/* Tabs Content */}
          <div className="flex-1 flex flex-col overflow-hidden">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
              {/* Tab Bar - Enhanced Enterprise Design */}
              <div className="border-b bg-[rgb(242,245,245)] dark:bg-muted/30">
                <TabsList className="h-auto p-0 bg-transparent border-0 gap-1">
                  <TabsTrigger 
                    value="overview"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Overview
                  </TabsTrigger>
                  <TabsTrigger 
                    value="values"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Values
                  </TabsTrigger>
                  <TabsTrigger 
                    value="settings"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Settings
                  </TabsTrigger>
                  <TabsTrigger 
                    value="compliance"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Compliance
                  </TabsTrigger>
                  <TabsTrigger 
                    value="supplier"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Supplier
                  </TabsTrigger>
                  <TabsTrigger 
                    value="equipment"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Equipment
                  </TabsTrigger>
                  <TabsTrigger 
                    value="custom-fields"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Custom Fields
                  </TabsTrigger>
                  <TabsTrigger 
                    value="notes"
                    className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                  >
                    Notes
                  </TabsTrigger>
                  {!isCreateMode && (
                    <>
                      <TabsTrigger 
                        value="documents"
                        className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                      >
                        Documents
                        {documentsCount > 0 && (
                          <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                            {documentsCount}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger 
                        value="alerts"
                        className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                      >
                        Alerts
                        {alertsCount > 0 && (
                          <Badge variant="secondary" className="ml-2 bg-muted/50 text-foreground hover:bg-muted/50">
                            {alertsCount}
                          </Badge>
                        )}
                      </TabsTrigger>
                      <TabsTrigger 
                        value="history"
                        className="relative rounded-none border-0 bg-transparent px-6 py-3 font-semibold text-muted-foreground shadow-none transition-all hover:bg-background/50 hover:text-foreground data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm dark:data-[state=active]:bg-background dark:data-[state=active]:text-black after:absolute after:bottom-0 after:left-0 after:right-0 after:h-0.5 after:bg-primary after:transition-all data-[state=active]:after:opacity-100 after:opacity-0 dark:data-[state=active]:after:bg-primary"
                      >
                        History
                      </TabsTrigger>
                    </>
                  )}
                </TabsList>
              </div>

              <div className="flex-1 overflow-auto">
                <TabsContent value="overview" className="m-0 p-6">
                  <AssetDetailOverview
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="values" className="m-0 p-6">
                  <AssetDetailValues
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="settings" className="m-0 p-6">
                  <AssetDetailSettings
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="compliance" className="m-0 p-6">
                  <AssetDetailCompliance
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="supplier" className="m-0 p-6">
                  <AssetDetailSupplier
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="equipment" className="m-0 p-6">
                  <AssetDetailEquipment
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="custom-fields" className="m-0 p-6">
                  <AssetDetailCustomFields
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                <TabsContent value="notes" className="m-0 p-6">
                  <AssetDetailNotes
                    asset={currentAsset}
                    onAssetUpdate={handleAssetUpdate}
                    isEditMode={isEditMode}
                  />
                </TabsContent>

                {!isCreateMode && (
                  <>
                    <TabsContent value="documents" className="m-0 p-6">
                      <DocumentsTab assetId={currentAsset.id} />
                    </TabsContent>

                    <TabsContent value="alerts" className="m-0 p-6">
                      <AssetAlertsTab assetId={currentAsset.id} assetName={currentAsset.name} asset={currentAsset} />
                    </TabsContent>

                    <TabsContent value="history" className="m-0 p-6">
                      <AssetDetailHistory asset={currentAsset} />
                    </TabsContent>
                  </>
                )}
              </div>
            </Tabs>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Asset</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this asset? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Disposal Wizard */}
      {showDisposalWizard && asset && (
        <DisposalWizard
          asset={asset}
          open={showDisposalWizard}
          onClose={() => setShowDisposalWizard(false)}
          onComplete={() => {
            setShowDisposalWizard(false);
            loadAssetDetail();
          }}
        />
      )}

      {/* Label Printing Modal */}
      {showLabelPrintingModal && asset && (
        <LabelPrintingModal
          open={showLabelPrintingModal}
          onClose={() => setShowLabelPrintingModal(false)}
          assets={[asset]}
        />
      )}
    </>
  );
}
